import { Button, Divider, Form, FormItemProps, FormProps, Input, Select } from 'antd';
import React, { useCallback, useMemo } from 'react';
import Highlighter from 'react-highlight-words';

import Icon from '@ant-design/icons';
import {
  ConditionContainer,
  ConditionRulesContainer,
  ConditionRulesTitle,
  RemovePlaceholder,
} from '@components/configure-network-panel/components/ConditionBuilder/ConditionRulesBuilder/styled';
import { IConditionBuilder } from '@components/configure-network-panel/components/ConditionBuilder/ConditionRulesBuilder/typed';
import AddRuleSvg from '@icons/mapping-panel/add-rule-icon.svg';
import DeleteSvg from '@icons/mapping-panel/delete-icon.svg';
import { keyToLabel } from '@src/emp-3.0/utils';

import styles from './index.less';

const formItemLayout: FormItemProps = {
  labelCol: { style: { padding: '8px 12px 8px 0', width: '142px' } },
  labelAlign: 'right',
  style: { margin: 0, padding: '8px 0 8px 47px' },
};

const TARGET_URL = 'target_url';
const configurationKey = [
  'target_url',
  'request_header',
  'request_payload',
  'response_header',
  'response_body',
];

const configRulesMappingTable = {
  target_url: 'Target Urls',
  request_header: 'Request Header',
  request_payload: 'Request Body',
  response_header: 'Response Header',
  response_body: 'Response Body',
};

const validateNameInput = (value) => {
  if (typeof value === 'string' && /^\s+$/g.test(value)) {
    return Promise.reject('The value cannot be all Spaces, carriage returns, tabs.');
  }
  return Promise.resolve();
};

const validateLegalInput = (value) => {
  if (typeof value === 'string' && (/^\s+$/g.test(value) || value.trim() === '*')) {
    return Promise.reject('The value cannot be all Spaces, carriage returns, tabs, or single *.');
  }
  return Promise.resolve();
};

const RequiredLabelFormItem = ({
  rules = [],
  label,
  required = false,
  ...resProps
}: FormItemProps) => {
  let myLabel = label;
  if (required || rules.some((rule) => typeof rule === 'object' && rule.required)) {
    myLabel = (
      <>
        {myLabel}
        <span style={{ color: 'red', padding: '0 2px' }}>*</span>
      </>
    );
  }
  return <Form.Item rules={rules} label={myLabel} required={required} {...resProps} />;
};

const ConditionRulesBuilder: React.FC<IConditionBuilder & FormProps> = ({
  mode = 'view',
  highlightRequiredText = false,
  searchKey,
  form,
  options,
  selectValue,
  onSelectChange,
  onFormChange,
  hideConfigName = false,
  ...rest
}) => {
  const isViewMode = useMemo(() => mode === 'view', [mode]);

  const showUrlCondition = useMemo(
    () => selectValue !== options?.[0].value,
    [selectValue, options],
  );

  // Generate dynamic form entries
  const generateFormItem = useCallback(
    (name: string) => {
      const searchWords = searchKey ? [searchKey] : [];
      return (
        <RequiredLabelFormItem
          key={name}
          label={configRulesMappingTable[name]}
          required={!isViewMode && name === TARGET_URL}
          {...formItemLayout}
        >
          <Form.List name={['configuration', name]}>
            {(fields, { add, remove }) => {
              return fields.map((field) => (
                <div
                  key={field.key}
                  style={{
                    display: 'flex',
                    gap: '16px',
                    padding: '8px 0',
                    overflowWrap: 'anywhere',
                  }}
                >
                  <Form.Item
                    {...field}
                    style={{ flex: 1, margin: 0 }}
                    rules={[
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (name === TARGET_URL) {
                            const curRules: string[] = getFieldValue(['configuration', name]);
                            const isValidRules =
                              curRules.length > 0 && curRules.some((item) => item !== '');
                            if (isValidRules) {
                              return validateLegalInput(value);
                            }
                            return Promise.reject(`${keyToLabel(name)} is required`);
                          } else {
                            return validateLegalInput(value);
                          }
                        },
                      }),
                    ]}
                  >
                    {isViewMode ? (
                      <Form.Item noStyle dependencies={['configuration', name, field.name]}>
                        {({ getFieldValue }) => {
                          return (
                            <Highlighter
                              autoEscape
                              textToHighlight={getFieldValue(['configuration', name, field.name])}
                              highlightStyle={{
                                background: '#FF0',
                                mixBlendMode: 'darken',
                                fontWeight: 500,
                              }}
                              unhighlightStyle={{ fontWeight: 500 }}
                              searchWords={searchWords}
                            />
                          );
                        }}
                      </Form.Item>
                    ) : (
                      <Input placeholder="Input here..." />
                    )}
                  </Form.Item>
                  {!isViewMode && (
                    <div>
                      {fields.length > 1 ? (
                        <Icon
                          component={DeleteSvg}
                          style={{
                            color: '#949494',
                            cursor: 'pointer',
                            width: '20px',
                            height: '20px',
                          }}
                          onClick={() => remove(field.name)}
                        />
                      ) : (
                        // placeholder element
                        <RemovePlaceholder />
                      )}
                      <Button
                        type="link"
                        icon={<Icon component={AddRuleSvg} />}
                        onClick={() => add('', field.name + 1)}
                      >
                        Add
                      </Button>
                    </div>
                  )}
                </div>
              ));
            }}
          </Form.List>
        </RequiredLabelFormItem>
      );
    },
    [isViewMode, form, searchKey],
  );

  const targetUrlFormItem = useCallback(
    () => (
      <div
        style={{
          background: 'rgba(0,0,0,0.02)',
          padding: `4px 12px ${showUrlCondition ? '16px' : '0'} 16px`,
        }}
      >
        <div style={{ padding: '12px 0' }}>
          <span style={{ color: '#0046FF', marginRight: '16px', fontWeight: 500 }}>
            URL Condition
          </span>
          {isViewMode ? (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                height: '32px',
                padding: '0 10px',
                border: '1px solid rgba(0,0,0,0.15)',
                borderRadius: '3px',
                fontSize: '12px',
              }}
            >
              {selectValue}
            </span>
          ) : (
            <Select
              style={{ width: 'fit-content', minWidth: 88 }}
              options={options}
              value={selectValue}
              onChange={onSelectChange}
              dropdownStyle={{
                minWidth: 'min-content',
              }}
            />
          )}
        </div>
        {showUrlCondition && generateFormItem(TARGET_URL)}
      </div>
    ),
    [isViewMode, showUrlCondition, selectValue, form, searchKey],
  );

  const genCollectionRulesItem = (type: 'request' | 'response' = 'request') => {
    return configurationKey
      .filter((item) => item !== TARGET_URL && item.includes(type))
      .map((key) => generateFormItem(key));
  };

  return (
    <ConditionContainer>
      <div style={{ height: '40px', fontWeight: 500 }}>
        Capture Network Request with following parameters:
      </div>
      <Form
        form={form}
        className={styles.conditionRulesForm}
        validateTrigger={false}
        // onValuesChange is fired when the form value changes,
        // onFieldsChange is fired when the value changes or validates.
        onValuesChange={onFormChange}
        {...rest}
      >
        <RequiredLabelFormItem
          label="Configuration Name"
          name="name"
          style={{
            padding: '8px 128px 16px 16px',
            margin: '0',
          }}
          labelCol={{ style: { width: '165px', paddingRight: '12px' } }}
          labelAlign="right"
          hidden={isViewMode || hideConfigName}
          rules={[
            {
              required: !isViewMode && !hideConfigName,
              type: 'string',
              max: 256,
              validator: (_, value) => {
                if (hideConfigName) return Promise.resolve();
                if (value) {
                  return validateNameInput(value);
                } else {
                  return Promise.reject('Configuration Name is required');
                }
              },
            },
          ]}
        >
          <Input placeholder="Input here..."  data-test-id="configurationNameInput"/>
        </RequiredLabelFormItem>
        <Form.Item
          name="configuration"
          style={{
            border: '1px solid rgba(0,0,0,0.15)',
            borderRadius: '4px',
          }}
        >
          {targetUrlFormItem()}
          <ConditionRulesContainer>
            <ConditionRulesTitle>
              <span style={{ fontWeight: 500 }}>Set Collection Rules </span>
              {!isViewMode && (
                <span
                  style={{
                    color: highlightRequiredText ? '#ff4d4f' : 'rgba(0, 0, 0, 0.65)',
                  }}
                >
                  (At least one rule is required)
                </span>
              )}
            </ConditionRulesTitle>
            {genCollectionRulesItem('request')}
            <div style={{ padding: '0 45px' }}>
              <Divider style={{ margin: '12px 0', borderColor: '#D9D9D9' }} />
            </div>
            {genCollectionRulesItem('response')}
          </ConditionRulesContainer>
        </Form.Item>
      </Form>
    </ConditionContainer>
  );
};

export default ConditionRulesBuilder;
